/**
 * attentionState - does this account have NEW work sitting on it right now?
 *
 * WHY THIS EXISTS
 * ---------------
 * A new order that nobody notices is the most expensive thing this dashboard
 * can produce. The bell already chimes once when a socket frame lands, but a
 * chime is a moment: if the operator is on another tab, in another app, or
 * simply looking away, the moment passes and the order sits. This module is
 * the other half - a state that stays true for as long as the work is
 * unactioned, so the tab title, the favicon and the banner all have one thing to follow. Audio
 * follows actual socket events; a queue count never triggers a tone.
 *
 * THE ONE RULE
 * ------------
 * The state clears when the COUNT reaches zero, never when someone dismisses
 * something. Dismissing a toast does not assign a rider or process an order.
 * That is why the source is utilities/menuBadges: every key there is a
 * server-derived count of work waiting, and every key there goes to zero when
 * the work is done. AdminNavbar's own bell count is deliberately NOT used -
 * it is incremented per socket frame and zeroed by "Clear all", so it can read
 * zero while five orders are still waiting for a rider.
 *
 * WHICH BADGES COUNT AS A NEW ARRIVAL
 * -----------------------------------
 * Only work that ARRIVED and must be picked up. Three of the six badge keys
 * qualify; the other three are deliberately excluded and each for its own
 * reason, recorded below so nobody "fixes" the omission:
 *
 *   inboxUnread         - a conversation, not an order arrival, and
 *                         OmnichannelInbox already badges the tab title with
 *                         it. Two modules writing document.title would fight.
 *   pendingSellers      - onboarding, not the order cycle. It already has a
 *                         pinned, non-dismissible row in the bell that
 *                         survives reloads, which is the right nudge for a
 *                         queue measured in hours rather than minutes.
 *   partnerFlaggedRiders- a standing condition (cash held over the limit), not
 *                         an arrival. It can sit above zero for days, so as an
 *                         alarm trigger it would never stop, and an alarm that
 *                         never stops is an alarm nobody hears.
 */

import { describeBadge, getMenuBadges, isKnownBadge } from "./menuBadges";
import { colors } from "../views/ThreePL/partnerTheme";

/**
 * The new-arrival signals, and where the work actually lives. The path is here
 * rather than in the banner because "where do I go to clear this" is a
 * property of what is being counted, exactly as the label is in menuBadges.
 * Paths match the `liveBadge` routes in routes.js.
 */
export const NEW_ARRIVAL_SIGNALS = Object.freeze({
  // Lusuku admin and staff: orders still sitting at PROCESSING.
  adminProcessingOrders: Object.freeze({ path: "/admin/paginatedOrdersTable" }),
  // 3PL partners and their staff: orders handed over and still without a rider.
  partnerNewAssigned: Object.freeze({ path: "/admin/partner-orders" }),
  // Brand owners: an inbound quote request is this vendor type's new order.
  brandOpenQuotes: Object.freeze({ path: "/admin/brand-quotes" }),
});

export const NEW_ARRIVAL_BADGES = Object.freeze(Object.keys(NEW_ARRIVAL_SIGNALS));

export const isNewArrivalBadge = (key) =>
  Object.hasOwn(NEW_ARRIVAL_SIGNALS, key);

/** Total pieces of new work waiting. Zero means there is nothing to nudge about. */
export const countNewArrivals = (counts = getMenuBadges()) =>
  NEW_ARRIVAL_BADGES.reduce((total, key) => {
    const n = Number(counts?.[key]) || 0;
    return n > 0 ? total + n : total;
  }, 0);

export const isAttentionActive = (counts = getMenuBadges()) =>
  countNewArrivals(counts) > 0;

/**
 * Everything the nudge needs, or null when there is nothing waiting. Null is
 * the whole "off" contract: no banner, no title flip, no favicon dot, no
 * visual reminder. Audio is handled separately by socket events.
 */
export const describeAttention = (counts = getMenuBadges()) => {
  const items = NEW_ARRIVAL_BADGES.map((key) => {
    // describeBadge returns null at zero and owns the spoken sentence, so the
    // banner reads with the same words as the sidebar chip and the tooltip.
    const badge = describeBadge(key, counts);
    if (!badge) return null;
    return {
      key,
      count: badge.count,
      label: badge.label,
      path: NEW_ARRIVAL_SIGNALS[key].path,
    };
  }).filter(Boolean);

  if (items.length === 0) return null;

  const count = items.reduce((total, item) => total + item.count, 0);
  return {
    active: true,
    count,
    items,
    /** The precise sentences, joined. "4 orders waiting to be processed". */
    message: items.map((item) => item.label).join(", "),
    /** Where "Open" goes: the signal with the most waiting. */
    path: items.reduce((a, b) => (b.count > a.count ? b : a)).path,
  };
};

/* ── The browser tab title ────────────────────────────────────────────────────
 * A backgrounded tab shows nothing but its title, so that is where a nudge has
 * to live for the case this feature exists for: the operator is in another
 * tab. The title ALTERNATES rather than sitting on the count, because a static
 * title is read once and then stops being seen.
 *
 * 1500ms per phase is 0.33Hz. It is text in a browser chrome, not a flash, but
 * it is kept slow for the same reason the CSS is: see the accessibility note
 * at the top of assets/css/lusuku-overrides.css. Do not lower it.
 */
export const TITLE_ALTERNATE_MS = 1500;

export const composeNudgeTitle = (count, baseTitle) =>
  `(${count}) New orders - ${baseTitle || "Lusuku"}`;

/**
 * A title alternator that can give the page back EXACTLY the title it found.
 * The base is captured on the first start, before anything is written, so a
 * restart can never capture a nudge title and bake it in permanently.
 */
export function createTitleNudge({ target = null, intervalMs = TITLE_ALTERNATE_MS } = {}) {
  let timer = null;
  let baseTitle = "";
  let captured = false;
  let nudgeTitle = "";
  let showingNudge = false;

  const doc = () => target || (typeof document === "undefined" ? null : document);

  const paint = (useNudge) => {
    const d = doc();
    if (!d) return;
    showingNudge = useNudge;
    d.title = useNudge ? nudgeTitle : baseTitle;
  };

  return {
    isRunning: () => timer !== null,
    baseTitle: () => baseTitle,
    /** Start alternating, or update the count of a run already going. */
    start(count) {
      const d = doc();
      const n = Number(count) || 0;
      if (!d || n <= 0) return;
      if (!captured) {
        baseTitle = d.title;
        captured = true;
      }
      nudgeTitle = composeNudgeTitle(n, baseTitle);
      if (timer === null) {
        paint(true);
        timer = setInterval(() => paint(!showingNudge), intervalMs);
      } else if (showingNudge) {
        paint(true); // refresh the number in place, without resetting the phase
      }
    },
    /** Stop and hand the page back the exact title it had. */
    stop() {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      showingNudge = false;
      const d = doc();
      if (d && captured) d.title = baseTitle;
    },
  };
}

/* ── The favicon dot ──────────────────────────────────────────────────────────
 * Same audience as the title: a backgrounded tab. The site icon is redrawn
 * onto a canvas with a dot in the corner and swapped in as a data URL, and the
 * original href is put back on stop. Every step is guarded and silent on
 * failure: a browser that will not give us a 2d context simply keeps its
 * normal icon, which is a cosmetic loss and nothing more.
 */
export function createFaviconDot({ target = null } = {}) {
  let link = null;
  let originalHref = null;
  let painted = false;

  const doc = () => target || (typeof document === "undefined" ? null : document);

  return {
    start() {
      const d = doc();
      if (!d || painted) return;
      try {
        link =
          d.querySelector('link[rel~="icon"]') ||
          d.querySelector('link[rel="shortcut icon"]');
        if (!link) return;
        if (originalHref === null) originalHref = link.getAttribute("href");
        const source = originalHref || "/favicon.png";
        const img = new Image();
        img.onload = () => {
          try {
            const size = 64;
            const canvas = d.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext && canvas.getContext("2d");
            if (!ctx) return;
            ctx.drawImage(img, 0, 0, size, size);
            const r = size * 0.24;
            const cx = size - r - 2;
            const cy = r + 2;
            // A ring of page-white first, so the dot reads on any icon.
            ctx.beginPath();
            ctx.arc(cx, cy, r, 0, Math.PI * 2);
            ctx.fillStyle = colors.white;
            ctx.fill();
            ctx.beginPath();
            ctx.arc(cx, cy, r * 0.72, 0, Math.PI * 2);
            ctx.fillStyle = colors.amber;
            ctx.fill();
            link.setAttribute("href", canvas.toDataURL("image/png"));
            painted = true;
          } catch (_e) {
            /* keep the normal icon */
          }
        };
        img.src = source;
      } catch (_e) {
        /* keep the normal icon */
      }
    },
    stop() {
      painted = false;
      if (!link || originalHref === null) return;
      try {
        link.setAttribute("href", originalHref);
      } catch (_e) {
        /* nothing to restore to */
      }
    },
  };
}

/** Guard for a typo in a future signal list: every signal must be a real badge. */
export const signalsAreKnownBadges = () => NEW_ARRIVAL_BADGES.every(isKnownBadge);
